import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { SampleViewModel } from "@/services/backend/samples/types/sample-view-model";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useSamplesDeleteManyMutation } from "@/services/backend/samples/api/sample-api";
import { useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  CommonDialogFooter,
  CommonDialogHeader,
} from "@/shared/components/dialog/dialog-common";
import { Kind } from "@/services/backend/samples/types/kind";
import { toast } from "sonner";
import t from "@/lang/lang";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";

export type SampleAssignment = {
  positionNumbers: number[];
};

export function DeleteSamplesDialog({
  samples,
  onSuccess,
  getAssignment,
}: {
  samples: SampleViewModel[];
  onSuccess: () => void;
  getAssignment?: (sample: SampleViewModel) => SampleAssignment | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [doDelete, { isLoading, error }] = useSamplesDeleteManyMutation();

  const assignmentsBySampleId = new Map<string, SampleAssignment | undefined>(
    samples.map((s) => [String(s.id), getAssignment?.(s)]),
  );
  const hasAnyAssignment = Array.from(assignmentsBySampleId.values()).some(
    (a) => a !== undefined && a.positionNumbers.length > 0,
  );

  const deleteSamples = () => {
    if (isLoading) return;

    doDelete({
      airMicrobialSampleSetIds: samples
        .filter((s) => s.kind === Kind.AirMicrobial)
        .map((s) => s.id),
      dustDensitySampleIds: samples
        .filter((s) => s.kind === Kind.DustDensity)
        .map((s) => s.id),
      surfaceMicrobialSampleSetIds: samples
        .filter((s) => s.kind === Kind.SurfaceMicrobial)
        .map((s) => s.id),
      waterSampleIds: samples
        .filter((s) => s.kind === Kind.Water)
        .map((s) => s.id),
    })
      .unwrap()
      .then(() => {
        onSuccess();
        setOpen(false);
        toast.success(t("Proben gelöscht"));
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button size="sm" variant="destructive">
          <Trash2 />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <CommonDialogHeader
          title={t("Proben löschen")}
          description={t("Die Proben werden unwiederruflich gelöscht.")}
        />
        <ul className="text-muted-foreground list-disc px-6 text-sm">
          {samples.map((s) => {
            const assignment = assignmentsBySampleId.get(String(s.id));
            const positions = assignment?.positionNumbers ?? [];
            return (
              <li key={s.id}>
                {s.number}
                {positions.length > 0 && (
                  <span className="text-destructive ml-2">
                    {t("Verknüpft mit Position(en)")}: {positions.join(", ")}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        <AssignedToAssessmentsAlert hasAnyAssignment={hasAnyAssignment} />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={deleteSamples}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}

function AssignedToAssessmentsAlert({
  hasAnyAssignment,
}: {
  hasAnyAssignment: boolean;
}) {
  if (hasAnyAssignment) {
    return (
      <Alert variant="destructive">
        <AlertTriangle />
        <AlertTitle>
          {t("Probe ist mit einer Beurteilung verknüpft")}
        </AlertTitle>
        <AlertDescription>
          {t(
            "Mindestens eine der ausgewählten Proben ist noch einer Position bzw. Beurteilung in der Inspektion zugeordnet. Empfohlen: Entfernen Sie die Beurteilung zuerst in der Inspektion. Sonst muss die verwaiste Beurteilung nach dem Löschen manuell aus der Inspektion entfernt werden.",
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert>
      <AlertTriangle />
      <AlertTitle>{t("Verbundene Inspektion")}</AlertTitle>
      <AlertDescription>
        {t(
          "Eine Probe kann mit einer Beurteilung in der Inspektion verknüpft sein. Wenn Sie die Probe löschen, ohne die Beurteilung zu entfernen, muss die verwaiste Beurteilung manuell aufgeräumt werden.",
        )}
      </AlertDescription>
    </Alert>
  );
}
